import React, { useState, useCallback } from "react";
import useShops from "../hooks/useShops";
import EditableTableCell from "./EditableTableCell";
import { useErrorStore } from "components";
import { useAuth } from "hooks/useAuth";
import { formatDate } from "utils";

// Прапорці, що дозволяють скриптам оновлювати ціну, наявність і знижку
const UPDATE_FLAGS = [
  { field: "is_update_price", label: "Ціна" },
  { field: "is_update_quantity", label: "Наявність" },
  { field: "is_update_discount", label: "Знижка" },
];

// Таблиця магазинів
const ShopsTable = React.memo(() => {
  const { shops, updateShop } = useShops();
  const showError = useErrorStore((state) => state.showError);
  const { isAllowed } = useAuth();
  const canEdit = isAllowed("shop-settings:edit");
  const [editing, setEditing] = useState({ id: null, field: null, value: "" });

  // Переводить обрану комірку в режим вводу тексту, зберігаючи її поточне значення
  const handleDoubleClick = useCallback((shopId, field, currentValue) => {
    setEditing({ id: shopId, field, value: String(currentValue ?? "") });
  }, []);

  // Перехоплює натискання клавіш для збереження (Enter) або скасування (Escape) змін
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        const { id, field, value } = editing;
        const trimmedValue = value.trim();

        if (trimmedValue === "") {
          showError("Поле не може бути порожнім");
          return;
        }

        if (field === "trap_sku" && !/^[A-Z]+-$/.test(trimmedValue)) {
          showError(
            "Префікс має містити лише великі літери та закінчуватись дефісом (напр. 'PRO-')",
          );
          return;
        }

        updateShop(id, { [field]: trimmedValue });
        setEditing({ id: null, field: null, value: "" });
      } else if (e.key === "Escape") {
        setEditing({ id: null, field: null, value: "" });
      }
    },
    [editing, updateShop, showError],
  );

  return (
    <div id="shops-table" className="shops-suppliers-tables">
      <h3>Таблиця магазинів</h3>
      <table className="suppliers-table">
        <thead>
          <tr>
            <th>ID (CRM ID)</th>
            <th>Назва магазину</th>
            <th>Приставка ловушки</th>
            <th>Токен</th>
            <th>Дійсний до</th>
            {UPDATE_FLAGS.map(({ field, label }) => (
              <th key={field} title="Дозволити скриптам оновлювати це поле">
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {shops.length === 0 ? (
            <tr>
              <td colSpan={5 + UPDATE_FLAGS.length} className="empty-row">
                Немає магазинів
              </td>
            </tr>
          ) : (
            shops.map((shop) => (
              <tr key={shop.id}>
                <td>
                  {shop.id} <span className="crm-id">({shop.crm_id})</span>
                </td>

                <EditableTableCell
                  item={shop}
                  field="name"
                  editing={editing}
                  onDoubleClick={handleDoubleClick}
                  onKeyDown={handleKeyDown}
                  setEditing={setEditing}
                  requiredPermission="shop-settings:edit"
                />

                <EditableTableCell
                  item={shop}
                  field="trap_sku"
                  editing={editing}
                  onDoubleClick={handleDoubleClick}
                  onKeyDown={handleKeyDown}
                  setEditing={setEditing}
                  requiredPermission="shop-settings:edit"
                  placeholder="Напр. PRO-"
                />

                <EditableTableCell
                  item={shop}
                  field="token"
                  editing={editing}
                  onDoubleClick={handleDoubleClick}
                  onKeyDown={handleKeyDown}
                  setEditing={setEditing}
                  requiredPermission="shop-settings:edit"
                  isSecret={true}
                  isCopyable={true}
                />

                <td
                  className={
                    new Date(shop.token_valid) < new Date() ? "text-danger" : ""
                  }
                >
                  {formatDate(shop.token_valid)}
                </td>

                {UPDATE_FLAGS.map(({ field }) => (
                  <td key={field}>
                    <input
                      className="focus-checkbox"
                      type="checkbox"
                      checked={!!shop[field]}
                      disabled={!canEdit}
                      onChange={(e) => updateShop(shop.id, { [field]: e.target.checked })}
                    />
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
});

export default ShopsTable;
